import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Bug, MessageCircle, Mail, CheckCircle2, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

const TYPES = [
  { v: "all", l: "Tutti" },
  { v: "chat", l: "Chat", icon: MessageCircle, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { v: "bug", l: "Bug", icon: Bug, cls: "bg-red-50 text-red-700 border-red-200" },
  { v: "email", l: "Email", icon: Mail, cls: "bg-sky-50 text-sky-700 border-sky-200" },
];
const STATUS = {
  open: ["Aperto", "bg-amber-50 text-amber-700 border-amber-200"],
  answered: ["Risposto", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  closed: ["Chiuso", "bg-slate-100 text-slate-500 border-slate-200"],
};

function TicketRow({ t, onChanged }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const meta = TYPES.find((x) => x.v === t.type) || TYPES[1];
  const Icon = meta.icon;
  const [stLabel, stCls] = STATUS[t.status] || STATUS.open;

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.post(`/super-admin/tickets/${t.id}/reply`, { text: reply.trim() }); setReply(""); toast.success("Risposta inviata nella chat dell'utente"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    setBusy(false);
  };
  const setStatus = async (status) => {
    try { await api.patch(`/super-admin/tickets/${t.id}/status`, { status }); onChanged(); }
    catch (e) { toast.error("Aggiornamento non riuscito"); }
  };

  return (
    <div data-testid={`ticket-${t.id}`} className="bg-card border border-slate-200/80 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900" data-testid="ticket-user">{t.user_name || "Utente"} <span className="text-xs font-normal text-slate-400">· {t.user_email}</span></p>
          <p className="text-xs text-slate-400" data-testid="ticket-date">{fmtDate(t.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="ticket-type" className={meta.cls}><Icon style={{ width: 12, height: 12 }} className="mr-1" />{meta.l}</Badge>
          <Badge variant="outline" data-testid="ticket-status" className={stCls}>{stLabel}</Badge>
        </div>
      </div>
      <p data-testid="ticket-text" className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{t.text}</p>
      {(t.replies || []).map((r, i) => (
        <p key={i} className="text-sm mt-2 ml-6 rounded-lg bg-emerald-50 text-emerald-900 px-3 py-2"><span className="block text-[10px] font-semibold text-emerald-600">Risposta · {fmtDate(r.at)}</span>{r.text}</p>
      ))}
      <div className="flex gap-2 mt-3 flex-wrap sm:flex-nowrap">
        <Input data-testid={`ticket-reply-input-${t.id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Rispondi all'utente (arriva nella sua chat)…" />
        <Button data-testid={`ticket-reply-send-${t.id}`} onClick={send} disabled={busy || !reply.trim()} className="shrink-0 font-semibold">Rispondi</Button>
        {t.status === "closed" ? (
          <Button variant="outline" data-testid={`ticket-reopen-${t.id}`} onClick={() => setStatus("open")} className="shrink-0"><RotateCcw style={{ width: 14, height: 14 }} className="mr-1" /> Riapri</Button>
        ) : (
          <Button variant="outline" data-testid={`ticket-close-${t.id}`} onClick={() => setStatus("closed")} className="shrink-0"><CheckCircle2 style={{ width: 14, height: 14 }} className="mr-1" /> Chiudi</Button>
        )}
      </div>
    </div>
  );
}

export default function TicketsPanel({ onChanged }) {
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data: d } = await api.get(`/super-admin/tickets?type=${type}&status=${status}`); setData(d); onChanged?.(); }
    catch (e) { toast.error("Errore caricamento ticket"); }
  };
  useEffect(() => { load(); }, [type, status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="tickets-panel">
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {TYPES.map((t) => (
          <button key={t.v} type="button" data-testid={`ticket-filter-${t.v}`} onClick={() => setType(t.v)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${type === t.v ? "bg-primary text-white border-primary" : "bg-card text-slate-600 border-slate-200 hover:border-primary/40"}`}>
            {t.l} {data && <span className="opacity-70">({data.counts[t.v] ?? 0})</span>}
          </button>
        ))}
        <span className="mx-2 h-5 w-px bg-slate-200" />
        {[["all", "Tutti gli stati"], ["open", "Aperti"], ["answered", "Risposti"], ["closed", "Chiusi"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`ticket-status-filter-${v}`} onClick={() => setStatus(v)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${status === v ? "bg-slate-900 text-white border-slate-900" : "bg-card text-slate-500 border-slate-200"}`}>{l}</button>
        ))}
      </div>
      {!data ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : data.tickets.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-card p-12 text-center"><p className="text-slate-400" data-testid="tickets-empty">Nessun ticket per questo filtro.</p></div>
      ) : (
        <div className="space-y-3">{data.tickets.map((t) => <TicketRow key={t.id} t={t} onChanged={load} />)}</div>
      )}
    </div>
  );
}
